import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import BillDetailsToolbarButton from 'components/bill/BillDetailsToolbarButton';
import {
  ConsultationsConsumerRenderProps,
  withConsultationsContext
} from 'components/consultations/ConsultationsContext';
import ToolbarButton from 'components/layout/ToolbarButton';
import { TreatmentsConsumerRenderProps, withTreatmentsContext } from 'components/treatments/TreatmentsContext';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouterProps } from 'react-router';
import { Icon } from 'semantic-ui-react';
import { getNumberOfXmlResponses } from 'services/mediDataServices';
import styled from 'styled-components';
import { Privilege } from 'ts-types/api.enums';
import { IndexSignature } from 'utils/praxTypes';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const ActionsToolbarWrapper = styled.div`
  padding: 0 0.425rem;
  margin-bottom: 0.7rem;
`;

const StyledToolbarBtn = styled(ToolbarButton)`
  &.clear-right-side {
    padding-right: 0 !important;
    margin-right: 0 !important;
  }
`;

const ButtonDivider = styled.div`
  display: inline-block;
  text-align: center;
  margin: 0 0.775rem;
  height: 1.4rem;
  border-left: 1px solid rgba(0, 0, 0, 0.175);
  cursor: default;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`;

interface Props extends AuthConsumerRenderProps,
    TreatmentsConsumerRenderProps,
    ConsultationsConsumerRenderProps,
    MessageSourceProps,
    RouterProps {

}

interface State extends IndexSignature {
  xmlResponses: string,
  cancelTokenSource: CancelTokenSource
}

const ToolbarDivider = () => {

  return (
      <ButtonDivider>&nbsp;</ButtonDivider>
  )
};

class ActionsToolbar extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();
    this.state = {
      cancelTokenSource,
      xmlResponses: ""
    }
  }

  componentDidMount() {
    this.getNumberOfXmlResponsesForDefaultTariff();
  }

  goToTreatmentOverview = () => this.props.history.push("/treatment-overview");
  goToAutomaticBilling = () => this.props.history.push("/automatic-billing");
  goToBillStorno = () => this.props.history.push("/storno/bill");
  goToPaymentStorno = () => this.props.history.push("/storno/payment");
  goToAutomaticPayment = () => this.props.history.push("/automatic-payment");
  goToManualPayment = () => this.props.history.push("/manual-payment");
  goToManualBillReminder = () => this.props.history.push("/manual-reminder");
  goToAutomaticBillReminder = () => this.props.history.push("/automatic-reminder");
  goToMediDataUpload = () => this.props.history.push("/medidata-upload");
  goToMediDataDownload = () => this.props.history.push("/medidata-download");
  goToMediDataNotifications = () => this.props.history.push("/medidata-notifications");
  goToDocumentCopies = () => this.props.history.push("/document-copies");
  goToCollectiveBills = () => this.props.history.push("/collective-bills")
  goToPatientEmails = () => this.props.history.push("/patient-emails");

  userHasPrivilege(privilege: Privilege): boolean {
    const {currentUser} = this.props;
    return currentUser!.userConfig.privileges.includes(privilege)
  }

  getNumberOfXmlResponsesForDefaultTariff = () => {

    const {cancelTokenSource} = this.state;
    const {currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;

    getNumberOfXmlResponses(mandateId, cancelTokenSource)
        .then(response => {
          this.setState({
            xmlResponses : response
          })
        })
        .catch(() => {
        })
        .finally(() => {
        });
  };

  render(): React.ReactNode {
    const {selectedTreatment, currentConsultations, getMessage, currentUser} = this.props;

    const toolbarButtonStyle = {padding: '0', paddingRight: '1rem'};
    return (
        <ActionsToolbarWrapper>
          <StyledToolbarBtn iconName='book'
                            iconSize='large'
                            className="clear-right-side"
                            onClick={this.goToTreatmentOverview}
                            dataTooltip={getMessage("header.navigation.treatmentOverview")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="book"/>
                                  <Icon corner="top right" name="zoom" color="grey"/>
                                </Icon.Group>
                            )}
          />
          <ToolbarDivider/>
          <BillDetailsToolbarButton
              currentTreatment={selectedTreatment}
              currentConsultations={currentConsultations}
              actionsToolbar={true}
          />
          <StyledToolbarBtn iconName='file alternate outline'
                            iconSize='large'
                            onClick={this.goToAutomaticBilling}
                            dataTooltip={getMessage("header.navigation.automaticBilling")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="file alternate outline"/>
                                  <Icon corner="top right" name="cog" color="grey"/>
                                </Icon.Group>
                            )}
          />
          <StyledToolbarBtn iconName='file alternate outline'
                            iconSize='large'
                            className="clear-right-side"
                            onClick={this.goToBillStorno}
                            dataTooltip={getMessage("header.navigation.cancelBill")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="file alternate outline"/>
                                  <Icon corner="top right" name="delete" color="grey"/>
                                </Icon.Group>
                            )}
          />
          <ToolbarDivider/>
          <StyledToolbarBtn iconName='address card outline'
                            iconSize='large'
                            onClick={this.goToMediDataUpload}
                            dataTooltip={getMessage("header.navigation.mediDataUpload")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            disabled={!this.userHasPrivilege(Privilege.PX_CORE_BILLS_XML)}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="upload"/>
                                </Icon.Group>
                            )}
          />
          <StyledToolbarBtn iconName='address card outline'
                            iconSize='large'
                            onClick={this.goToMediDataDownload}
                            dataTooltip={getMessage("header.navigation.mediDataDownload")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            disabled={!this.userHasPrivilege(Privilege.PX_CORE_BILLS_XML)}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="download"/>
                                  <Icon corner="top right" size="large" color="red">{this.state.xmlResponses}</Icon>
                                </Icon.Group>
                            )}
          />
          <StyledToolbarBtn iconName='address card outline'
                            iconSize='large'
                            className="clear-right-side"
                            onClick={this.goToMediDataNotifications}
                            dataTooltip={getMessage("header.navigation.mediDataNotifications")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            disabled={!this.userHasPrivilege(Privilege.PX_CORE_BILLS_XML)}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="list"/>
                                </Icon.Group>
                            )}
          />
          <ToolbarDivider/>
          <StyledToolbarBtn iconName='money bill alternate outline'
                            iconSize='large'
                            onClick={this.goToAutomaticPayment}
                            dataTooltip={getMessage("header.navigation.automaticPayment")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="money bill alternate outline"/>
                                  <Icon corner="top right" name="cog" color="grey"/>
                                </Icon.Group>
                            )}
          />
          <StyledToolbarBtn iconName='money bill alternate outline'
                            iconSize='large'
                            onClick={this.goToManualPayment}
                            dataTooltip={getMessage("header.navigation.manualPayment")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="money bill alternate outline"/>
                                  <Icon corner="top right" name="magnify" color="grey"/>
                                </Icon.Group>
                            )}
          />
          <StyledToolbarBtn iconName='money bill alternate outline'
                            iconSize='large'
                            className="clear-right-side"
                            onClick={this.goToPaymentStorno}
                            dataTooltip={getMessage("header.navigation.cancelPayment")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="money bill alternate outline"/>
                                  <Icon corner="top right" name="delete" color="grey"/>
                                </Icon.Group>
                            )}
          />
          <ToolbarDivider/>
          <StyledToolbarBtn iconName='money bill alternate outline'
                            iconSize='large'
                            onClick={this.goToManualBillReminder}
                            dataTooltip={getMessage("manualReminder")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="bell outline"/>
                                  {/*<Icon corner="top right" name="money bill alternate outline" color="grey" />*/}
                                </Icon.Group>
                            )}
          />
          <StyledToolbarBtn iconName='money bill alternate outline'
                            iconSize='large'
                            className="clear-right-side"
                            onClick={this.goToAutomaticBillReminder}
                            dataTooltip={getMessage("header.navigation.automaticReminder")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="bell outline"/>
                                  <Icon corner="top right" name="cog" color="grey"/>
                                </Icon.Group>
                            )}
          />
          <ToolbarDivider/>
          <StyledToolbarBtn iconName='copy outline'
                            iconSize='large'
                            className="clear-right-side"
                            onClick={this.goToDocumentCopies}
                            dataTooltip={getMessage("documentCopies")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="copy outline"/>
                                </Icon.Group>
                            )}
          />
          <ToolbarDivider/>
          <StyledToolbarBtn iconName='paste'
                            iconSize='large'
                            className="clear-right-side"
                            onClick={this.goToCollectiveBills}
                            dataTooltip={getMessage("collectiveBill")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="paste"/>
                                </Icon.Group>
                            )}
          />
          <ToolbarDivider/>
            <StyledToolbarBtn iconName='envelope outline'
                              iconSize='large'
                              className="clear-right-side"
                              onClick={this.goToPatientEmails}
                              dataTooltip={getMessage("patientEmails")}
                              toolbarButtonStyle={toolbarButtonStyle}
                              customTrigger={(
                                  <Icon.Group size="large">
                                    <Icon name="envelope outline"/>
                                  </Icon.Group>
                              )}
            />

          {/*<ToolbarDivider/>
          <StyledToolbarBtn iconName='eye'
                            iconSize='large'
                            onClick={() => getInvalidConsultations(this.state.cancelTokenSource)}
                            dataTooltip={getMessage("Show Invalid Consultations")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="eye" color="red"/>
                                </Icon.Group>
                            )}
          />
          <StyledToolbarBtn iconName='wrench'
                            iconSize='large'
                            onClick={() => fixInvalidConsultations(this.state.cancelTokenSource)}
                            dataTooltip={getMessage("Fix Invalid Consultations")}
                            toolbarButtonStyle={toolbarButtonStyle}
                            customTrigger={(
                                <Icon.Group size="large">
                                  <Icon name="wrench" color="red"/>
                                </Icon.Group>
                            )}
          />*/}
        </ActionsToolbarWrapper>
    );
  }
}

export default withRouterWorkaround(withAuthContext(withMessages(withTreatmentsContext(withConsultationsContext(ActionsToolbar)))));
